import React, { FC, ReactElement, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MENUS } from '../../../constants/menu-types';
import { QUICK_ENROLL_BKGCOLOR, TITLE_BKGCOLOR, TITLE_FGCOLOR, UI_ELEMENT_SELECT_MENU_ITEM } from '../../../constants/widget-constants';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useDashboardContext } from '../../../contexts/dashboard/DashboardContext';
import { useLocale } from '../../../contexts/localeContext';
import ShortcutIcon from '../../../images/Shortcut Arrow.svg';
import { SettingData } from '../../../models/SettingData';
import { Widget } from '../../../models/Widget';
import WizardSideBarStep from '../../../models/WizardSideBarStep';
import BaseWidget from '../base-widget';
import ColorStep from '../color-step';
import EditWidgetModal from '../edit-wiget-modal';
import MenuStep from './add/menu-step';
import SummaryStep from './add/summary-step';
import { saveSettingData } from '../../../services/settingDataService';
import { showError } from '../../../services/utilities/ServiceError';
import { toast } from 'react-toastify';

interface ShortcutWidgetProps {
    widget: Widget;
    previewMode?: boolean;
}

const ShortcutWidget: FC<ShortcutWidgetProps> = ({ widget, previewMode = false }) => {
    const history = useHistory();
    const { translate } = useLocale();
    const [isOpen, setIsOpen] = useState(false);
    const dashboard = useDashboardContext();
    const [selectedMenu, setSelectedMenu] = useState<string>(translate.byKey(TranslationKeys.pending));
    const [step, setStep] = useState<number>(0);

    const steps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.menu),
            description: selectedMenu,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.widgetConfig),
            description: '',
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const onNext = () => {
        setStep(step + 1);
    }

    const onBack = () => {
        setStep(step - 1);
    }

    const content: ReactElement[] = [
        <MenuStep onNext={onNext} setSelectedMenu={setSelectedMenu} />,
        <ColorStep onNext={onNext} onBack={onBack} />,
        <SummaryStep onBack={onBack} selectedMenu={selectedMenu} />
    ];

    const onSave = (values: any) => {
        const settingDatasToEdit: SettingData[] = buildSettingDatas(values);

        Promise.all(settingDatasToEdit.map((settingData) => {
            return saveSettingData(settingData);
        })).then(() => {
            dashboard.onEditReload(values);
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            onClose();
        });
    }

    const buildSettingDatas = (values: any) => {
        const original: Widget | undefined = dashboard.widgets.find(widget => widget.id === values.id);
        const settingDatasToEdit: SettingData[] = [];

        if (original && original.settingDatasRefs && original.foregroundColor) {
            appendSettingToEdit(settingDatasToEdit, original.titleBackgroundColor, values.titleBackgroundColor, TITLE_BKGCOLOR, original.settingDatasRefs);
            appendSettingToEdit(settingDatasToEdit, original.titleForegroundColor, values.titleForegroundColor, TITLE_FGCOLOR, original.settingDatasRefs);
            appendSettingToEdit(settingDatasToEdit, original.foregroundColor, values.foregroundColor, QUICK_ENROLL_BKGCOLOR, original.settingDatasRefs);

            const menu = original.relationships.find(relationship => relationship['eSysnameLookup'] === UI_ELEMENT_SELECT_MENU_ITEM);
            if (menu) {
                appendSettingToEdit(settingDatasToEdit, menu['sdValstring'], values.menuId, UI_ELEMENT_SELECT_MENU_ITEM, original.settingDatasRefs);
            }
        }

        return settingDatasToEdit;
    }

    const appendSettingToEdit = (settingDatasToEdit: SettingData[], original: string, current: string, eSysname: string, settingDataRefs: any[]) => {
        if (original !== current) {
            const settingDataRef = settingDataRefs.find(settingData => settingData.eSysname === eSysname);
            if (settingDataRef) {
                settingDatasToEdit.push({
                    id: settingDataRef.id,
                    sdValstring: current
                });
            }
        }
    }

    const onEdit = (key: string) => {
        dashboard.setIsDraggable(false);
        setIsOpen(true);
    }

    const getMenu = () => {
        const menuInfo = widget.relationships.find(relationship => relationship['eSysnameLookup'] === UI_ELEMENT_SELECT_MENU_ITEM);
        if (menuInfo) {
            return MENUS.find(menu => menu.id === menuInfo['menuId']);
        }

        return undefined;
    }

    const getTitle = () => {
        const menu = getMenu();
        if (menu) {
            return translate.byKey(menu.translationKey) ?? menu.name;
        }

        return translate.byKey(TranslationKeys.shortcut);
    }

    const onNavigate = () => {
        const menu = getMenu();

        if (menu) {
            history.push(menu.path);
        }
    }

    const onClose = () => {
        dashboard.setIsDraggable(true);
        setIsOpen(false);
        setStep(0);
    }

    return <div className={`${previewMode ? 'w-32' : ''}`} style={{ width: '102.5px'}}>
    {/*return <div className={`${previewMode ? 'w-32' : ''}`}>*/}
        <BaseWidget
            titleBackgroundColor={widget.titleBackgroundColor}
            titleForegroundColor={widget.titleForegroundColor}
            title={getTitle()}
            onEdit={onEdit}
            widgetKey={widget.id.toString()}
            previewMode={previewMode}>
            <div className={`h-24`} style={{ backgroundColor: widget.foregroundColor, width: "100%", height: "70px" }}>
                <button disabled={previewMode} className='flex flex-wrap w-full h-full justify-center content-center' type='button' onClick={onNavigate}>
                    <img src={ShortcutIcon} className='p-2' alt='' style={{ height: '60px' }} />
                </button>
            </div>
        </BaseWidget >
        <EditWidgetModal
            isOpen={isOpen}
            onClose={onClose}
            steps={steps}
            content={content}
            step={step}
            title={translate.byKey(TranslationKeys.shortcut)}
            widget={widget}
            onSave={onSave} />
    </div>
}

export default ShortcutWidget;
